import React, { useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import { t } from "../utils/i18n";
import { useApp } from "../context/AppContext";
import CatalogPicker from "./CatalogPicker";
import { PowerIcon } from "./SkillIcons";

const initials = (name?: string) => (name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

// Popup affiché une seule fois, au tout premier passage sur Home après inscription — quoi
// qu'il arrive (fermeture, "Publier", ou champs laissés vides), l'arrivée est annoncée aux
// membres proches : soit avec ce que l'utilisateur a personnalisé, soit avec le message par
// défaut. Fermer le popup n'annule donc jamais la diffusion, ça ne fait que la laisser par défaut.
export default function WelcomePopup({ user, onDone }: { user: any; onDone: (patch: any) => void }) {
  const { lang } = useApp();
  const [message, setMessage] = useState("");
  const [bio, setBio] = useState("");
  const [passions, setPassions] = useState<any[]>(user?.passions || []);
  const [powers, setPowers] = useState<any[]>(user?.powers || []);
  const [showPassionPicker, setShowPassionPicker] = useState(false);
  const [showPowerPicker, setShowPowerPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const addPassion = async (label: string) => {
    const data = await api.post("/users/me/passions", { label, icon: "⭐" }).catch(() => null);
    if (data?.passion) setPassions(prev => [...prev, data.passion]);
  };
  const addPower = async (label: string) => {
    const data = await api.post("/users/me/powers", { label, icon: "⚡" }).catch(() => null);
    if (data?.power) setPowers(prev => [...prev, data.power]);
  };

  const finish = () => {
    if (submitting) return;
    setSubmitting(true);
    api.post("/users/me/welcome", { message: message.trim() || undefined, bio: bio.trim() || undefined })
      .then(() => onDone({ welcome_shown: true, ...(bio.trim() ? { bio: bio.trim() } : {}), passions, powers }))
      .catch(() => onDone({ welcome_shown: true }))
      .finally(() => setSubmitting(false));
  };

  const pill: React.CSSProperties = { padding: "7px 14px", borderRadius: 20, backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 600 };
  const addPill: React.CSSProperties = { padding: "7px 14px", borderRadius: 20, border: "1.5px dashed #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 12.5, fontWeight: 600, cursor: "pointer" };
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 };
  const textarea: React.CSSProperties = { width: "100%", minHeight: 56, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none", marginBottom: 16, boxSizing: "border-box", resize: "vertical" as const, fontFamily: "inherit" };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport. Corrige du même
  // coup les deux feuilles imbriquées (passion/power picker), qui héritent du vrai viewport une
  // fois leur ancêtre commun sorti du conteneur scrollable.
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <span style={{ fontSize: 19, fontWeight: 800 }}>{t(lang, "onboarding_welcome_title")}</span>
          <button onClick={finish} disabled={submitting} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", flexShrink: 0 }}>✕</button>
        </div>
        <p style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, marginBottom: 20 }}>
          {t(lang, "onboarding_welcome_desc_p1")}{user?.city ? ` (${user.city})` : ""}{t(lang, "onboarding_welcome_desc_p2")}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: "#F8F8F8", borderRadius: 14, padding: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(user?.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{message.trim() || `${user?.name} ${t(lang, "onboarding_welcome_joined_suffix")}`}</div>
            {user?.city && <div style={{ fontSize: 11.5, color: "#999" }}>{user.city}</div>}
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#0a0a0a", border: "1px solid #E0E0E0", borderRadius: 14, padding: "4px 10px", flexShrink: 0 }}>{t(lang, "see_profile")}</span>
        </div>

        <div style={label}>{t(lang, "onboarding_welcome_message_label")}</div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={300} placeholder={t(lang, "onboarding_welcome_message_placeholder")} style={textarea} />

        <div style={label}>{t(lang, "onboarding_welcome_bio_label")}</div>
        <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} placeholder={t(lang, "onboarding_welcome_bio_placeholder")} style={textarea} />

        <div style={{ ...label, marginBottom: 8 }}>{t(lang, "onboarding_welcome_tags_label")}</div>
        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 22 }}>
          {passions.map((p: any) => <span key={p.id} style={pill}>⭐ {p.label}</span>)}
          {powers.map((p: any) => (
            <span key={p.id} style={{ ...pill, display: "flex", alignItems: "center", gap: 5 }}>
              <PowerIcon size={11} color="white" /> {p.label}
            </span>
          ))}
          <button onClick={() => setShowPassionPicker(true)} style={addPill}>{t(lang, "onboarding_welcome_add_passion")}</button>
          <button onClick={() => setShowPowerPicker(true)} style={addPill}>{t(lang, "onboarding_welcome_add_power")}</button>
        </div>

        <button onClick={finish} disabled={submitting} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14.5, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? t(lang, "onboarding_welcome_publishing") : t(lang, "onboarding_welcome_publish_btn")}
        </button>
      </div>

      {showPassionPicker && (
        <div onClick={() => setShowPassionPicker(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, maxHeight: "80vh", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 12px" }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "onboarding_welcome_pick_passion_title")}</span>
              <button onClick={() => setShowPassionPicker(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <CatalogPicker type="passion" mode="multiple" selected={passions.map((p: any) => p.label)} onSelect={addPassion} />
          </div>
        </div>
      )}

      {showPowerPicker && (
        <div onClick={() => setShowPowerPicker(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, maxHeight: "80vh", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 12px" }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "onboarding_welcome_pick_power_title")}</span>
              <button onClick={() => setShowPowerPicker(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <CatalogPicker type="power" mode="multiple" selected={powers.map((p: any) => p.label)} onSelect={addPower} />
          </div>
        </div>
      )}
    </div>
  ,
    document.body
  );
}
